/**
 * Google Identity Services and Facebook Login, loaded on demand.
 *
 * Both SDKs hand the provider token to the page, which trades it for our own JWT through the
 * `socialSignin` mutation. Nothing here leaves the page: no redirect, no callback route, and the
 * `state` below is not OAuth state.
 *
 * @typedef {{ provider: string, clientId: string, sdkVersion: ?string }} SocialAuthProvider
 */

const GOOGLE_SCRIPT_ID = "google-identity-services";
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const FACEBOOK_SCRIPT_ID = "facebook-javascript-sdk";
const FACEBOOK_SCRIPT_SRC = "https://connect.facebook.net/en_US/sdk.js";
const GOOGLE_BUTTON_REVEAL_TIMEOUT_MS = 5000;
const GOOGLE_BUTTON_MAX_WIDTH = 400;

const getGoogleIdentity = () => window.google;

const pendingScripts = new Map();

/**
 * @param {string} id
 * @param {string} src
 * @param {() => boolean} ready tells whether the SDK global is already usable
 * @returns {Promise<void>}
 */
const loadScript = (id, src, ready) => {
  if (ready()) {
    return Promise.resolve();
  }

  const pending = pendingScripts.get(id);

  if (pending) {
    return pending;
  }

  const attempt = new Promise((resolve, reject) => {
    /* A tag that is still in the document but has not made the SDK ready is a failed load (a
       blocked host, a tracker blocker) and never fires `load` again, so waiting on it would hang
       forever. Replace it and fetch again. */
    document.getElementById(id)?.remove();

    const script = document.createElement("script");

    script.addEventListener(
      "load",
      () =>
        ready()
          ? resolve()
          : reject(new Error(`Provider SDK ${id} did not initialize`)),
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        script.remove();
        reject(new Error(`Unable to load provider SDK ${id}`));
      },
      { once: true },
    );
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }).finally(() => pendingScripts.delete(id));

  pendingScripts.set(id, attempt);

  return attempt;
};

let googleClientId = null;
let googleButtonSequence = 0;
const googleCredentialHandlers = new Map();
const googleButtonOwners = new WeakMap();

const hasGoogleButtonSize = (frame) => parseFloat(frame.style.height) > 0;

/* `renderButton` inserts a 0x0 iframe and the SDK sizes it only once the document inside has
   painted the button and reported its size; the frame is still 0x0 when its `load` fires, and the
   size follows only after a round trip to Google. So the host stays hidden until the frame has a
   height. A slow size report is still a button, not a failure: the timeout reveals it. `cancel`
   drops the observer and the timer of a superseded frame; it settles the wait so the render that
   owns it can finish and find itself outdated. */
const waitForGoogleButtonFrame = (parent) => {
  const frame = parent.querySelector("iframe");

  if (!frame || hasGoogleButtonSize(frame)) {
    return { settled: Promise.resolve(), cancel: () => undefined };
  }

  let finish = () => undefined;
  const settled = new Promise((resolve) => {
    finish = resolve;
  });
  const cancel = () => {
    observer.disconnect();
    window.clearTimeout(timer);
    finish();
  };
  const observer = new MutationObserver(() => {
    if (hasGoogleButtonSize(frame)) cancel();
  });
  const timer = window.setTimeout(cancel, GOOGLE_BUTTON_REVEAL_TIMEOUT_MS);

  observer.observe(frame, { attributes: true, attributeFilter: ["style"] });

  return { settled, cancel };
};

/**
 * Renders Google's own sign-in button into `parent` and reports the ID token it returns.
 *
 * @param {HTMLElement} parent
 * @param {string} clientId
 * @param {?string} locale BCP 47 code for the button text, e.g. `en`
 * @param {(credential: string) => void} onCredential
 * @param {(ready: boolean) => void} [onReadinessChange]
 * @returns {Promise<() => void>} disposes the button and its observers
 */
export const renderGoogleSignInButton = async (
  parent,
  clientId,
  locale,
  onCredential,
  onReadinessChange,
) => {
  await loadScript(GOOGLE_SCRIPT_ID, GOOGLE_SCRIPT_SRC, () =>
    Boolean(getGoogleIdentity()?.accounts?.id),
  );

  const googleIdentity = getGoogleIdentity();

  if (!googleIdentity) {
    throw new Error("Google Identity Services is unavailable");
  }

  if (googleClientId !== clientId) {
    googleCredentialHandlers.clear();
    googleIdentity.accounts.id.initialize({
      client_id: clientId,
      callback: ({ credential, state }) => {
        if (credential && state) {
          googleCredentialHandlers.get(state)?.(credential);
        }
      },
    });
    googleClientId = clientId;
  }

  /* Not OAuth state: a routing key, so a remounted button's credential reaches the live handler
     instead of a stale closure. */
  const state = `lux-google-${++googleButtonSequence}`;

  googleCredentialHandlers.set(state, onCredential);
  googleButtonOwners.set(parent, state);

  let disposed = false;
  let renderedWidth = 0;
  let renderSequence = 0;
  let resizeFrame;
  let pendingFrame;

  const render = async () => {
    const sequence = ++renderSequence;
    /* Only the newest render of a live button may report readiness either way: a superseded one
       has had its iframe removed by the next `replaceChildren()` and must not touch the host. */
    const isCurrent = () =>
      !disposed &&
      sequence === renderSequence &&
      googleButtonOwners.get(parent) === state;

    pendingFrame?.cancel();

    const width = Math.min(
      GOOGLE_BUTTON_MAX_WIDTH,
      Math.max(1, Math.round(parent.offsetWidth)),
    );

    renderedWidth = Math.round(parent.offsetWidth);
    onReadinessChange?.(false);
    parent.replaceChildren();

    try {
      googleIdentity.accounts.id.renderButton(parent, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        logo_alignment: "left",
        state,
        width,
        /* Left unset, the SDK takes the language from the browser or the signed-in Google
           account, which leaves the button in a different language from the page around it. */
        ...(locale ? { locale } : {}),
      });

      const frame = waitForGoogleButtonFrame(parent);

      pendingFrame = frame;
      await frame.settled;

      if (isCurrent()) onReadinessChange?.(true);
    } catch (error) {
      if (isCurrent()) onReadinessChange?.(false);
      throw error;
    }
  };

  await render();

  /* The button is not responsive: the SDK paints it at the width it was given. A breakpoint cross
     or an orientation change has to re-render, and every re-render is a round trip to Google, so
     the frame callback collapses a burst of resizes into one. */
  const resizeObserver =
    typeof ResizeObserver === "undefined"
      ? undefined
      : new ResizeObserver(([entry]) => {
          const nextWidth = Math.round(entry.contentRect.width);

          if (!nextWidth || nextWidth === renderedWidth) return;

          renderedWidth = nextWidth;

          if (resizeFrame !== undefined) {
            window.cancelAnimationFrame(resizeFrame);
          }

          resizeFrame = window.requestAnimationFrame(() => {
            // A failed re-render has already reported itself through `onReadinessChange`.
            void render().catch(() => undefined);
          });
        });

  resizeObserver?.observe(parent);

  return () => {
    disposed = true;
    resizeObserver?.disconnect();

    if (resizeFrame !== undefined) {
      window.cancelAnimationFrame(resizeFrame);
    }

    pendingFrame?.cancel();
    googleCredentialHandlers.delete(state);

    if (googleButtonOwners.get(parent) === state) {
      googleButtonOwners.delete(parent);
      parent.replaceChildren();
    }
  };
};

let facebookAppId = null;
let facebookGraphApiVersion = null;

/**
 * @param {string} appId
 * @param {string} graphApiVersion pinned Graph API version, as the backend reports it
 * @returns {Promise<void>}
 */
export const prepareFacebookLogin = async (appId, graphApiVersion) => {
  await loadScript(FACEBOOK_SCRIPT_ID, FACEBOOK_SCRIPT_SRC, () =>
    Boolean(window.FB),
  );

  if (!window.FB) {
    throw new Error("Facebook Login is unavailable");
  }

  if (facebookAppId !== appId || facebookGraphApiVersion !== graphApiVersion) {
    window.FB.init({
      appId,
      cookie: false,
      version: graphApiVersion,
      xfbml: false,
    });
    facebookAppId = appId;
    facebookGraphApiVersion = graphApiVersion;
  }
};

/**
 * Opens Facebook's login window.
 *
 * @returns {Promise<?string>} the access token, or null when the traveller dismissed the window
 */
export const requestFacebookAccessToken = () => {
  const facebook = window.FB;

  if (!facebook || !facebookAppId || !facebookGraphApiVersion) {
    return Promise.reject(new Error("Facebook Login is not initialized"));
  }

  return new Promise((resolve) => {
    facebook.login(
      (response) => resolve(response.authResponse?.accessToken ?? null),
      { scope: "email", return_scopes: true },
    );
  });
};
